import { ChamadoResponseStatus, ChamadoResponseUrgencia } from '@/api/generated/model';

/**
 * Rótulos, cores e formatação do módulo de suporte.
 *
 * Funções puras e mapas — nada de componente aqui. É o que permite testá-los
 * sem montar tela, e é o mesmo arranjo de `features/discussoes/rotulos.ts`.
 */

export const STATUS = Object.values(ChamadoResponseStatus);
export const URGENCIAS = Object.values(ChamadoResponseUrgencia);

/**
 * ⚠️ Os rótulos falam com o SOLICITANTE, não descrevem o enum. "ABERTO" quer
 * dizer, para quem pediu ajuda, "ninguém pegou ainda" — e
 * `AGUARDANDO_SOLICITANTE` vira "Aguardando você", que é a única forma de ele
 * entender que a bola está com ele.
 */
export const ROTULO_STATUS: Record<string, string> = {
  ABERTO: 'Aguardando atendimento',
  EM_ANDAMENTO: 'Em andamento',
  AGUARDANDO_SOLICITANTE: 'Aguardando você',
  CONCLUIDO: 'Concluído',
  CANCELADO: 'Cancelado',
};

/** Como o mesmo status se chama na fila de quem ATENDE. */
export const ROTULO_STATUS_ADMIN: Record<string, string> = {
  ABERTO: 'Na fila',
  EM_ANDAMENTO: 'Em andamento',
  AGUARDANDO_SOLICITANTE: 'Aguardando solicitante',
  CONCLUIDO: 'Concluído',
  CANCELADO: 'Cancelado',
};

export const CLASSE_STATUS: Record<string, string> = {
  ABERTO: 'text-amber-600 border-amber-600/40',
  EM_ANDAMENTO: 'text-brand border-brand/40',
  AGUARDANDO_SOLICITANTE: 'text-sky-600 border-sky-600/40',
  CONCLUIDO: 'text-emerald-600 border-emerald-600/40',
  CANCELADO: 'text-destructive border-destructive/40',
};

export const ROTULO_URGENCIA: Record<string, string> = {
  BAIXA: 'Baixa',
  MEDIA: 'Média',
  ALTA: 'Alta',
  CRITICA: 'Crítica',
};

/**
 * Dica por nível, exibida no `<Select>` de abertura.
 *
 * Sem elas, todo mundo marca CRÍTICA — e uma fila em que tudo é crítico não
 * tem prioridade nenhuma. Descrever o IMPACTO, e não a pressa, é o que dá ao
 * usuário um critério para escolher.
 */
export const DICA_URGENCIA: Record<string, string> = {
  BAIXA: 'Incomoda, mas consigo trabalhar',
  MEDIA: 'Atrapalha o meu dia',
  ALTA: 'Estou travado em uma tarefa importante',
  CRITICA: 'O sistema está parado para mim',
};

export const CLASSE_URGENCIA: Record<string, string> = {
  BAIXA: 'text-muted-foreground border-muted-foreground/40',
  MEDIA: 'text-sky-600 border-sky-600/40',
  ALTA: 'text-amber-600 border-amber-600/40',
  CRITICA: 'text-destructive border-destructive/40',
};

/** Texto da pílula de evento no meio da conversa. */
export const ROTULO_EVENTO: Record<string, string> = {
  ASSUMIDO: 'Atendimento iniciado',
  TRANSFERIDO: 'Transferido',
  URGENCIA_ALTERADA: 'Urgência alterada',
  PREVISAO_DEFINIDA: 'Previsão de conclusão',
  CORRECAO_AGENDADA: 'Correção agendada',
  AGUARDANDO_SOLICITANTE: 'Aguardando resposta',
  CONCLUIDO: 'Concluído',
  CANCELADO: 'Cancelado',
};

/** Chamado encerrado não aceita mais mensagem — o composer some. */
export function estaEncerrado(status: string): boolean {
  return status === ChamadoResponseStatus.CONCLUIDO || status === ChamadoResponseStatus.CANCELADO;
}

/** Só chamado vivo faz polling de 2s. Um concluído nunca mais muda. */
export function estaVivo(status: string | undefined): boolean {
  return status !== undefined && !estaEncerrado(status);
}

/** `2026-09-16T21:00:00.000Z` → `16/09/2026 18:00`. */
export function formatarDataHora(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

/**
 * `2026-09-22` → `22/09/2026`.
 *
 * ⚠️ `timeZone: 'UTC'` é load-bearing. `correcaoPrevistaEm` é uma DATA pura
 * (`@db.Date`), e `new Date('2026-09-22')` é interpretado como meia-noite UTC:
 * formatado no fuso local de Brasília (−03), vira 21/09. O solicitante
 * cobraria a correção um dia antes do prometido.
 */
export function formatarData(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
}

/** "agora", "há 5 min", "há 3 h", ou a data — para a coluna "atualizado". */
export function formatarRelativo(iso: string): string {
  const minutos = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);

  if (minutos < 1) return 'agora';
  if (minutos < 60) return `há ${minutos} min`;

  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `há ${horas} h`;

  const dias = Math.floor(horas / 24);
  if (dias < 7) return `há ${dias} ${dias === 1 ? 'dia' : 'dias'}`;

  return new Date(iso).toLocaleDateString('pt-BR');
}

/**
 * Frase única com o que foi prometido ao solicitante.
 *
 * Os dois prazos juntos de propósito: do ponto de vista de quem espera, eles
 * respondem à mesma pergunta ("quando isso acaba?"), e dois cartões separados
 * fariam a tela parecer ter duas promessas concorrentes.
 */
export function resumoDosPrazos(chamado: {
  previsaoConclusaoEm: string | null;
  correcaoPrevistaEm: string | null;
}): string | null {
  const partes = [
    chamado.previsaoConclusaoEm
      ? `Previsão de conclusão: ${formatarDataHora(chamado.previsaoConclusaoEm)}`
      : null,
    chamado.correcaoPrevistaEm
      ? `Correção prevista para ${formatarData(chamado.correcaoPrevistaEm)}`
      : null,
  ].filter((parte): parte is string => parte !== null);

  return partes.length > 0 ? partes.join(' · ') : null;
}
